import QueryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import System from '../../config/SystemConfig';
import useLogger from '../../containers/common-components/hooks/useLogger';
import {
  CommunicationMessageTypes,
  LessonControlMessage,
  LessonState,
  NotsSystem,
  SharedTabs,
} from '../../helpers/constants';
import useCommunicator from '../../hooks/useCommunicator';
import useInterval from '../../hooks/useInterval';
import useReportMonitorReady from '../../hooks/useReportMonitorReady';
import {
  finishAttemptAC,
  listenInTab_AC,
  sendToParent_AC,
  showHintNoActionButtons_Ac,
  showMultimediaAndHintReview_AC,
  updateExecutionMode_AC,
} from '../../store/actions/executor.actions';
import {
  IControlMessage,
  isLessonStateControlMessage,
  LessonControlTypes,
} from '../../types/communicator-types';
import { minToMillis } from '../../utils/dateUtils';
import {
  LessonDidNotStartView,
  LessonStoppedView,
} from './LessonControllerHelpers';
import LessonStartedView from './LessonStartedView';

export interface ILessonControlPanelProps {}

const getNotsSystem = (system: string): NotsSystem => {
  if (system === 's80') {
    return NotsSystem.S80;
  } else if (system === 'catiz3') {
    return NotsSystem.CATIZ;
  } else {
    return NotsSystem.HERMSYS;
  }
};

export default function LessonControlPanel(props: ILessonControlPanelProps) {
  const [estimatedTime, setEstimatedTime] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState<any>(null);
  const [intervalDelay, setIntervalDelay] = useState<any>(null);

  const [status, setStatus] = useState(LessonState.STARTED);
  const [isLessonStarted, setIsLessonStarted] = useState(false);

  const communicator = useCommunicator<IControlMessage>(
    CommunicationMessageTypes.ControlMessage
  );

  const lessonDefinition = useSelector(
    (state: any) => state.resources.lessonDefinition
  );
  const dialogMonitor = useSelector(
    (state: any) => state.executor.preferences.dialogMonitor
  );

  const multiMediaAndHintReviews = useSelector(
    (state: any) => state.executor.multimediaAndHintReviews
  );

  const executorResources = useSelector((state: any) => state.resources);

  const { mode, system } = QueryString.parse(window.location.search);

  const dispatch = useDispatch();

  const reportMonitorReady = useReportMonitorReady();

  useLogger('LessonControlPanel.tsx', 'LessonControlPanel');

  useEffect(() => {
    mode && dispatch(updateExecutionMode_AC(`${mode}`));
  }, [mode]);

  useEffect(() => {
    system &&
      System.setSystem(getNotsSystem(system as string)).setCurrentTab(
        SharedTabs.LESSON_CONTROL_PANEL
      );
  }, [system]);

  useEffect(() => {
    if (lessonDefinition?.estimatedTime && mode !== 'demo') {
      setEstimatedTime(lessonDefinition.estimatedTime);
      setTimeRemaining(minToMillis(lessonDefinition.estimatedTime));
      setIntervalDelay(1000);
    }
  }, [lessonDefinition]);

  useInterval(() => {
    const newTime = Math.max(timeRemaining - 1000, 0);
    if (newTime === 0) {
      // Time ran out, bye bye dude, you quit now.
      setIntervalDelay(null);
      dispatch(
        sendToParent_AC(
          showHintNoActionButtons_Ac(
            'Time limit exceeded for the lesson. Try again'
          )
        )
      );
      setTimeout(() => {
        stopLesson();
      }, 1000);
    }
    setTimeRemaining(Math.max(timeRemaining - 1000, 0));
  }, intervalDelay);

  useEffect(() => {
    if (mode !== 'demo') {
      if (!isLessonStarted || status !== LessonState.STARTED) {
        setIntervalDelay(null);
      } else if (estimatedTime !== 0) {
        setIntervalDelay(1000);
      }
    } else {
      setIntervalDelay(null);
    }
  }, [isLessonStarted, status]);

  useEffect(() => {
    reportMonitorReady('lesson-controller');
    dispatch(listenInTab_AC());
  }, []);

  useEffect(() => {
    communicator?.receiveMessage((msg) => {
      if (isLessonStateControlMessage(msg)) {
        if (msg.lessonState === LessonState.STARTED) {
          setIsLessonStarted(true);
        }
        if (msg.lessonState === LessonState.STOPPED) {
          setStatus(LessonState.STOPPED);
          setIsLessonStarted(false);
        }
      }
    });
  }, [communicator]);

  const pauseLesson = (x: boolean) => {
    const newStatus = x ? LessonState.PAUSED : LessonState.STARTED;
    setStatus(newStatus);
    communicator?.postMessage({
      lessonState: newStatus,
      type: LessonControlTypes.LessonStateControl,
    });
  };

  const stopLesson = () => {
    setStatus(LessonState.STOPPED);
    communicator?.postMessage({
      lessonState: LessonState.STOPPED,
      type: LessonControlTypes.LessonStateControl,
    });
    setIsLessonStarted(false);
    dispatch(finishAttemptAC({ abort: true }));
  };

  const exitPlayer = () => {
    communicator?.postMessage({
      controlMessage: LessonControlMessage.EXIT_PLAYER,
      type: LessonControlTypes.LessonControl,
      monitor: '',
    });
    setTimeout(() => {
      window.close();
    }, 500);
  };

  const replayLesson = () => {
    communicator?.postMessage({
      controlMessage: LessonControlMessage.REPLAY_LESSON,
      type: LessonControlTypes.LessonControl,
      monitor: '',
    });
  };

  const updateDialogMonitor = () => {
    communicator?.postMessage({
      controlMessage: LessonControlMessage.UPDATE_DIALOG_MONITOR,
      monitor: dialogMonitor === 'upper' ? 'lower' : 'upper',
      type: LessonControlTypes.LessonControl,
    });
  };

  const showMediaAndReviewHintAction = (index) => {
    dispatch(
      showMultimediaAndHintReview_AC({
        index,
      })
    );
  };

  if (!isLessonStarted && status === LessonState.STOPPED) {
    return (
      <LessonStoppedView replayLesson={replayLesson} exitPlayer={exitPlayer} />
    );
  }

  if (!isLessonStarted) {
    return <LessonDidNotStartView />;
  }

  return (
    <LessonStartedView
      mode={mode}
      timeRemaining={timeRemaining}
      estimatedTime={estimatedTime}
      status={status}
      system={system}
      pauseLesson={pauseLesson}
      stopLesson={stopLesson}
      dialogMonitor={dialogMonitor}
      updateDialogMonitor={updateDialogMonitor}
      multiMediaAndHintReviews={multiMediaAndHintReviews}
      showMediaAndReviewHintAction={showMediaAndReviewHintAction}
      executorResources={executorResources}
    />
  );
}
